/**
 * Tab 1 of the "Данные" page — ClearML dataset hierarchy (superuser-only).
 *
 * Zone → dataset. Shows the LATEST version with a download/open control, and —
 * if a local copy of an OLDER version is still around — surfaces it separately
 * ("Локальная копия: vX") with open/delete. MULTIPLE local copies may be active
 * at once (different people / datasets); each active copy is the lock for its
 * own dataset — the training automation skips a dataset while a copy is checked
 * out. The annotation kind (столы / аудио) is shown per dataset so it's clear
 * how it will be labeled.
 */
import { useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR from 'swr';
import { Loader2, Download, Trash2, AlertTriangle, ChevronRight, ChevronDown, HardDriveDownload } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';
import BootstrapButton from './BootstrapButton';
import {
  getHierarchy, startDownload, deleteLocalCopy, datasetKind,
  type ZoneNode, type DatasetNode, type DatasetVersionNode,
} from '@/api/datasets';
import { Tooltip } from '@/components/ui/tooltip';

interface Props {
  /** Called when a download is started/opened, so the parent can switch to the annotation tab. */
  onDownloadStarted?: (downloadId: number) => void;
  /** Open the pooled annotator for one domain (no download involved). The domain is
   *  passed because the two label very different things — tables are three stages of
   *  binary heads, shelves are one fullness percentage — so they are separate tools
   *  behind the same button. */
  onOpenPool?: (domain: PoolDomain) => void;
}

// 'boxes' — очередь боксов детектора; у неё нет ClearML-датасета, только вкладка разметки.
export type PoolDomain = 'table' | 'shelf' | 'boxes' | 'audio';

/** The pooled datasets that replaced the per-zone ones — one per domain. */
const POOL_PROJECT = 'ml_models_lifecycle';
const POOLS: { domain: PoolDomain; dataset: string; plugin: string; kindKey: string }[] = [
  { domain: 'table', dataset: 'table-occupancy-crops', plugin: 'table_control',
    kindKey: 'data.kind.table_crops' },
  { domain: 'shelf', dataset: 'shelf-percentage-crops', plugin: 'empty_shelf',
    kindKey: 'data.kind.shelf_percentage' },
  { domain: 'audio', dataset: 'audio-transcripts', plugin: 'speech_to_text',
    kindKey: 'data.kind.audio_transcripts' },
];

// The per-zone ClearML tree is hidden: those datasets are historical (labels live in
// Postgres pools now) and the other kinds were never migrated, so showing them invites
// labelling into a tool nothing reads. Flip to restore it — the rendering below is
// untouched, and `hierarchyKey` starts fetching again on its own.
const SHOW_LEGACY_DATASETS = false;
// Only fetch the tree when something renders it. It is a per-zone walk of ClearML —
// 28 zones x 2 datasets = 56 sequential REST calls, ~7s — and the component used to pay
// all of it behind a full-page spinner and then throw every byte away, because the three
// cards above come from POOLS, a constant. A null SWR key skips the request entirely.
const hierarchyKey = SHOW_LEGACY_DATASETS ? 'mml-hierarchy' : null;

export default function DatasetHierarchy({ onDownloadStarted, onOpenPool }: Props) {
  const { t } = useT();
  const { data, error, isLoading, mutate } = useSWR(
    hierarchyKey,
    () => getHierarchy().then(r => r.data),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Multiple copies may be active at once — just start the download (no replace
  // prompt). The backend is idempotent if this exact version is already staged.
  const download = async (folderId: string, ds: DatasetNode, v: DatasetVersionNode) => {
    setBusy(`dl:${v.version_id}`);
    try {
      const r = await startDownload(folderId, ds.name, v.version_id);
      await mutate();
      onDownloadStarted?.(r.data.id);
    } finally {
      setBusy(null);
    }
  };

  // Open button colour: green when the copy is ready, amber while it downloads.
  const openBtnCls = (status: string) =>
    status === 'ready'
      ? 'bg-emerald-600 text-white hover:bg-emerald-700'
      : 'bg-amber-500 text-white hover:bg-amber-600';

  const removeLocal = async (id: number) => {
    if (!confirm(t('data.annotate.deleteConfirm'))) return;
    setBusy(`rm:${id}`);
    try { await deleteLocalCopy(id); await mutate(); }
    finally { setBusy(null); }
  };

  if (isLoading) return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;
  if (error && !data) {
    return <ServerStatusNotice variant="card" error={error} onRetry={() => mutate()} />;
  }

  const zones = SHOW_LEGACY_DATASETS ? (data?.zones ?? []) : [];
  // No early "empty" return: the pooled dataset below is ALWAYS there, and hiding the
  // legacy tree emptied `zones` — which used to blank the whole page, pinned card and
  // all. Emptiness is a property of the legacy section, so it is reported inside it.

  return (
    <div className="space-y-2">
      {/* Refreshes the legacy tree and nothing else — the pool cards are static. */}
      {SHOW_LEGACY_DATASETS && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => mutate(getHierarchy(true).then(r => r.data))}>
            {t('data.refresh')}
          </Button>
        </div>
      )}

      {/* The pooled datasets: ONE dataset per domain covering every zone, labels in
          Postgres. They are not part of the per-zone hierarchy below (different project)
          and are never downloaded — labelling reads them live — so each gets a pinned
          card with a single "open the annotator" action. */}
      {POOLS.map(p => (
        <Card variant="section" key={p.domain}>
          <CardContent className="flex flex-wrap items-center gap-3 p-3">
            <div className="flex flex-col">
              <span className="font-mono text-sm">{p.dataset}</span>
              <span className="text-xs text-muted-foreground">{POOL_PROJECT} · {p.plugin}</span>
            </div>
            <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
              {t(p.kindKey)}
            </span>
            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] text-emerald-800">
              {t('data.pool.badge')}
            </span>
            <Button size="sm" className="ml-auto" onClick={() => onOpenPool?.(p.domain)}>
              {t('data.pool.open')}
            </Button>
          </CardContent>
        </Card>
      ))}

      {SHOW_LEGACY_DATASETS && !zones.length && (
        <div className="text-sm text-muted-foreground">{t('data.empty')}</div>
      )}

      {zones.filter((z: ZoneNode) =>
        z.datasets.some((ds: DatasetNode) => datasetKind(ds.name) !== 'table_crops'),
      ).map((z: ZoneNode) => {
        // Key on folder_id (always unique + present): the global declined-alerts
        // node has zone_id=null, which would collide / warn if used as a key.
        const open = expanded[z.folder_id] ?? true;
        return (
          <Card key={z.folder_id} variant="section">
            <CardContent className="space-y-2 p-3">
              <div className="flex w-full items-center gap-2">
                <button
                  className="flex flex-1 items-center gap-2 text-left font-medium"
                  onClick={() => setExpanded(p => ({ ...p, [z.folder_id]: !open }))}
                >
                  {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  {z.zone_name}
                  <span className="text-xs text-muted-foreground">({z.project_name})</span>
                </button>
                {/* Real zones only — the global declined-alerts node has zone_id=null
                    and nothing to bootstrap. */}
                {z.zone_id !== null && <BootstrapButton zoneId={z.zone_id} />}
              </div>
              {/* Per-zone TABLE datasets are hidden: they were superseded by the pooled
                  dataset above and would only be confusing to label into. Other domains
                  (audio, shelf) still use the per-zone download flow, so they stay.
                  Drop this filter once the per-zone table datasets are deleted. */}
              {open && z.datasets.filter((ds: DatasetNode) => datasetKind(ds.name) !== 'table_crops')
                .map((ds: DatasetNode) => {
                const latest = ds.versions[0];
                // A live local copy of any version (at most one across the tool).
                const activeV = ds.versions.find(v => v.downloaded);
                const localIsOlder = activeV && latest && activeV.version_id !== latest.version_id;
                return (
                  <div key={ds.name} className="ml-6 space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-mono">{ds.name}</span>
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                        {t(`data.kind.${datasetKind(ds.name)}`)}
                      </span>
                      {ds.error && (
                        <Tooltip content={ds.error}><span className="text-xs text-destructive">
                          <AlertTriangle className="inline h-3 w-3" />
                        </span></Tooltip>
                      )}
                    </div>
                    <div className="ml-4 space-y-1.5">
                      {!latest && <div className="text-xs text-muted-foreground">{t('data.noVersions')}</div>}
                      {latest && (
                        <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
                          <div className="flex flex-col">
                            <span className="text-xs text-muted-foreground">{t('data.latestVersion')}</span>
                            <span className="font-mono text-sm">{latest.version ?? latest.version_id.slice(0, 10)}</span>
                          </div>
                          {latest.downloaded ? (
                            <div className="ml-auto flex items-center gap-2">
                              <Button
                                size="sm"
                                className={openBtnCls(latest.downloaded.status)}
                                onClick={() => onDownloadStarted?.(latest.downloaded!.id)}
                              >
                                {t('data.open')} <span className="ml-1 opacity-80">[{latest.downloaded.status}]</span>
                              </Button>
                              <Button
                                variant="outline" size="sm"
                                disabled={busy === `rm:${latest.downloaded.id}`}
                                onClick={() => removeLocal(latest.downloaded!.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="ml-auto flex items-center gap-2">
                              {latest.last_status && (
                                <span className="text-xs text-muted-foreground">{t('data.last')}: {latest.last_status}</span>
                              )}
                              <Button
                                variant="default" size="sm"
                                disabled={busy === `dl:${latest.version_id}`}
                                onClick={() => download(z.folder_id, ds, latest)}
                              >
                                {busy === `dl:${latest.version_id}`
                                  ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                                  : <Download className="mr-1 h-4 w-4" />}
                                {t('data.download')}
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                      {/* Local copy of an older version still on the server. */}
                      {localIsOlder && (
                        <div className="flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
                          <HardDriveDownload className="h-4 w-4 text-amber-600" />
                          <div className="flex flex-col">
                            <span className="text-xs text-amber-700">{t('data.localCopy')}</span>
                            <span className="font-mono text-sm">
                              {activeV!.version ?? activeV!.version_id.slice(0, 10)}{' '}
                              <span className="opacity-70">[{activeV!.downloaded!.status}]</span>
                            </span>
                          </div>
                          <div className="ml-auto flex items-center gap-2">
                            <Button
                              size="sm"
                              className={openBtnCls(activeV!.downloaded!.status)}
                              onClick={() => onDownloadStarted?.(activeV!.downloaded!.id)}
                            >
                              {t('data.open')} <span className="ml-1 opacity-80">[{activeV!.downloaded!.status}]</span>
                            </Button>
                            <Button
                              variant="outline" size="sm"
                              disabled={busy === `rm:${activeV!.downloaded!.id}`}
                              onClick={() => removeLocal(activeV!.downloaded!.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
